import React, { useState, useEffect } from 'react';
import * as XLSX from 'xlsx';
import axios from 'axios';
import Table from '../../Table/Table';
import apiClient from '../../../pages/keycloak/axiosInstance';

const DamagedAnalysis = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [responseMessage, setResponseMessage] = useState(null);
  const projectId = localStorage.getItem('selectedProject') || '';
  const baseURL = process.env.REACT_APP_API_BASE_URL;

  useEffect(() => {
    const fetchDamagedModules = async () => {
      if (!projectId) {
        setResponseMessage({
          type: 'error',
          text: 'No project selected',
        });
        return;
      }

      setLoading(true);
      try {
        const formData = new FormData();
        formData.append('projectId', projectId);

        const response = await apiClient.post(`get_damaged_module_data`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });

        if (response.data.status === 'error') {
          setResponseMessage({
            type: 'error',
            text: response.data.message,
          });
          setLoading(false);
          return;
        }

        const moduleData = response.data.data;
        const transformedRows = moduleData.map((module, index) => ({
          id: index + 1,
          Date: module.timestamp ? new Date(module.timestamp).toLocaleDateString() : '',
          Module_Serial_No: module.Module_No,
          Pallet_No: module.Pallet_No,
          Binning: module.Binning,
          Watt_Peak: module.Module_Wattage,
          Damaged_During: module.damaged_during,
          MRN: module.upload_mrn ? module.upload_mrn : [],
          Evidence: module.upload_evidence ? module.upload_evidence : [],
          Video: module.upload_video ? module.upload_video : [],
          Claim_From: module.claim_from,
          Contractor: module.contractor,
          Reason: module.reason,
          Submitted_By: module.submitted_by,
        }));

        setRows(transformedRows);
        setResponseMessage({
          type: 'success',
          text: response.data.message || 'Damaged modules data fetched successfully',
        });
        setLoading(false);
      } catch (err) {
        setResponseMessage({
          type: 'error',
          text: err.response?.data?.message || err.message || 'Failed to fetch damaged modules data',
        });
        setLoading(false);
      }
    };

    fetchDamagedModules();
  }, [projectId]);

  useEffect(() => {
    if (responseMessage) {
      const timer = setTimeout(() => {
        setResponseMessage(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [responseMessage]);

  const downloadFiles = async (urls, prefix) => {
    for (let index = 0; index < urls.length; index++) {
      const url = urls[index];
      const link = document.createElement('a');
      link.href = url;
      link.download = `${prefix}_${index + 1}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      // Add a delay to ensure sequential downloads
      await new Promise(resolve => setTimeout(resolve, 500));
    }
  };

  const tableHeaders = [
    { dbParam: 'id', displayName: 'Sr.No', className: 'w-12' },
    { dbParam: 'Date', displayName: 'Date', className: 'w-24' },
    { dbParam: 'Module_Serial_No', displayName: 'Module Serial No', className: 'w-32' },
    { dbParam: 'Pallet_No', displayName: 'Pallet No', className: 'w-28' },
    { dbParam: 'Binning', displayName: 'Binning', className: 'w-20' },
    { dbParam: 'Watt_Peak', displayName: 'Watt Peak', className: 'w-20' },
    { dbParam: 'Damaged_During', displayName: 'Damaged During', className: 'w-32' },
    { dbParam: 'MRN', displayName: 'MRN', className: 'w-28' },
    { dbParam: 'Evidence', displayName: 'Evidence', className: 'w-28' },
    { dbParam: 'Video', displayName: 'Video', className: 'w-28' },
    { dbParam: 'Claim_From', displayName: 'Claim From', className: 'w-28' },
    { dbParam: 'Contractor', displayName: 'Contractor', className: 'w-28' },
    { dbParam: 'Reason', displayName: 'Reason', className: 'w-40' },
    { dbParam: 'Submitted_By', displayName: 'Submitted By', className: 'w-24' },
  ];

  const tableData = rows.map((row) => ({
    id: row.id,
    Date: row.Date,
    Module_Serial_No: row.Module_Serial_No,
    Pallet_No: row.Pallet_No,
    Binning: row.Binning,
    Watt_Peak: row.Watt_Peak,
    Damaged_During: row.Damaged_During,
    MRN: row.MRN.length > 0 ? (
      <button
        onClick={() => downloadFiles(row.MRN, `MRN_${row.Module_Serial_No}`)}
        className="text-blue-600 hover:text-blue-800 underline"
      >
        {row.MRN.length === 1 ? 'DOWNLOAD' : `DOWNLOAD ALL (${row.MRN.length})`}
      </button>
    ) : (
      'N/A'
    ),
    Evidence: row.Evidence.length > 0 ? (
      <button
        onClick={() => downloadFiles(row.Evidence, `Evidence_${row.Module_Serial_No}`)}
        className="text-blue-600 hover:text-blue-800 underline"
      >
        {row.Evidence.length === 1 ? 'DOWNLOAD' : `DOWNLOAD ALL (${row.Evidence.length})`}
      </button>
    ) : (
      'N/A'
    ),
    Video: row.Video.length > 0 ? (
      <button
        onClick={() => downloadFiles(row.Video, `Video_${row.Module_Serial_No}`)}
        className="text-blue-600 hover:text-blue-800 underline"
      >
        {row.Video.length === 1 ? 'DOWNLOAD' : `DOWNLOAD ALL (${row.Video.length})`}
      </button>
    ) : (
      'N/A'
    ),
    Claim_From: row.Claim_From,
    Contractor: row.Contractor,
    Reason: row.Reason,
    Submitted_By: row.Submitted_By,
  }));

  const downloadExcel = () => {
    const worksheet = XLSX.utils.json_to_sheet(
      rows.map((row) => ({
        'Sr.No': row.id,
        Date: row.Date,
        'Module Serial No': row.Module_Serial_No,
        'Pallet No': row.Pallet_No,
        Binning: row.Binning,
        'Watt Peak': row.Watt_Peak,
        'Damaged During': row.Damaged_During,
        MRN: row.MRN.length > 0 ? row.MRN.join(', ') : 'N/A',
        Evidence: row.Evidence.length > 0 ? row.Evidence.join(', ') : 'N/A',
        Video: row.Video.length > 0 ? row.Video.join(', ') : 'N/A',
        'Claim From': row.Claim_From,
        Contractor: row.Contractor,
        Reason: row.Reason,
        'Submitted By': row.Submitted_By,
      }))
    );
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Damaged Modules');
    XLSX.writeFile(workbook, 'Damaged_Modules_Analysis.xlsx');
  };

  return (
    <div className="container mx-auto p-6 bg-gray-100 min-h-screen">
      <div className="w-full mx-auto">
        <h1 className="text-3xl font-bold text-gray-800 mb-6 text-center">
          Damaged Modules Analysis
        </h1>

        {responseMessage && (
          <div
            className={`p-4 mb-4 rounded-lg shadow-lg transition-opacity duration-500 ${
              responseMessage.type === 'success'
                ? 'bg-green-500 text-white'
                : 'bg-red-500 text-white'
            }`}
          >
            {responseMessage.text}
          </div>
        )}

        <div className="bg-white p-6 rounded-lg shadow-md">
          {loading ? (
            <div className="flex justify-center items-center">
              <svg
                className="animate-spin h-8 w-8 text-blue-600"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
              <span className="ml-2 text-gray-700">Loading data...</span>
            </div>
          ) : (
            <>
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-semibold text-gray-700">Module Details</h2>
                <button
                  onClick={downloadExcel}
                  className="bg-gray-800 hover:bg-gray-900 text-white font-bold py-2 px-4 rounded-lg transition-colors duration-200"
                  disabled={rows.length === 0}
                >
                  Download Excel
                </button>
              </div>
              <div className="overflow-x-auto">
                <Table
                  tableHeaders={tableHeaders}
                  tableData={tableData}
                  order="asc"
                  orderBy="id"
                  handleSort={(dbParam) => {
                    const sortedRows = [...rows].sort((a, b) => {
                      const aValue = a[dbParam] || '';
                      const bValue = b[dbParam] || '';
                      return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
                    });
                    setRows(sortedRows);
                  }}
                />
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default DamagedAnalysis;